import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChatInput } from 'features/chat/components/ChatInput';
import { McpServiceAuthModal } from 'features/chat/components/McpServiceAuthModal';
import { AssistantFileViewer } from 'features/chat/components/AssistantFileViewer';
import {
  DashboardCanvas,
  DashboardChatPrompt,
  DashboardChatWorkingCursor,
  DashboardPreview,
} from 'features/chat/components/DashboardCanvas';
import { isHtmlAttachment } from 'features/chat/utils/assistant-file-preview';
import { VoiceCallDockedBar } from 'features/chat/components/VoiceCallDockedBar';
import { useEndVoiceCallOnChatChange, useVoiceCall } from 'features/chat/hooks/use-voice-call';
import { useVoiceCallStore } from 'state/store/chat/use-voice-call-store';
import { isVoiceCallEnabled } from 'config/feature-flags';
import { useMcpServiceChatStore } from 'state/store/chat/use-mcp-service-chat-store';
import { useAssistantFileViewerStore } from 'state/store/chat/use-assistant-file-viewer-store';
import { ChatMessages } from 'features/chat/components/ChatMessages';
import { ConversationUnavailable } from 'features/chat/components/conversation-unavailable';
import { LoaderCircle, TriangleAlert } from 'lucide-react';
import { Globe, MediaImage, EditPencil, Internet } from 'iconoir-react';
import { CreateAgentIcon } from 'components/blocks/layout/icons/create-agent-icon';
import { ChatPageParams } from './hooks/useConversationById';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useChatStore } from '../../state/store/chat/use-chat-store';
import { HttpError } from 'lib/https';
import type { IntegrationSource } from '../../services/sse-service';
import { useIsMobile } from '../../hooks/use-mobile';
import {
  Attachment,
  ButtonIds,
  FileMetadata,
  initialFeaturesState,
  Mention,
  type MessageSkillMention,
} from 'features/chat/types/chat';
import { useModelStore } from '../../state/store/chat/use-model-store';
import { useToast } from '../../hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import { useGetLlmModel } from 'hooks/use-llm-model';
import { useSubscriptionStore } from 'state/store/subscription';
import { Button } from 'components/ui/button';
import { WelcomeDialog } from 'components/blocks/welcome-dialog/welcome-dialog';
import { useQueryClient } from '@tanstack/react-query';
import {
  invalidateAgentConversationList,
  prependAgentConversationToCache,
  removeAgentConversationFromCache,
  syncConversationRenameCaches,
  wasConversationDeletedLocally,
} from 'features/chat/utils/agent-sidebar-sync';
import { CHAT_MAIN_COLUMN_CLASS, CHAT_MESSAGES_SCROLL_CLASS } from 'constant/chat-layout';
import { cn } from 'lib/utils';
import PaymentGracePeriodModal from 'features/chat/components/PaymentGracePeriodModal';
import NameRequiredModal from 'features/chat/components/NameRequiredModal';
import { NoPlanCard } from 'features/subscription/components/no-plan-card';
import { useMyPlan } from 'features/subscription/hooks/use-seats';
import { trackEvent } from 'analytics/tracker';
import { EVENTS, InitialPromptType } from 'analytics/events';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { getRandomGreeting } from 'utils/greeting-messages';
import { getConversationAccessErrorMessage, shouldShowConversationUnavailable } from 'utils/conversation-access-error';
import {
  buildChatConversationPath,
  buildChatLandingPath,
  runLockedNewChatInvitation,
} from 'utils/chat-utilities';
import { useLanguageContext } from 'i18n/language-context';
import { useInviteAssistant, useInviteUsers } from 'features/group-chat/hooks/use-invite';
import { useGetAgentById, useGetAgents } from 'features/chat/hooks/use-agents';
import { useAgentSelectionStore } from 'state/store/chat/use-agent-selection-store';
import { findBuddyAgent } from 'features/chat/utils/find-buddy-agent';
import { defaultCreateAgentChatPrompt } from 'features/chat/utils/create-agent-chat-navigation';
import { OnboardingChatPanel } from 'features/onboarding/components/onboarding-chat-panel';
import { useOnboardingJourney } from 'features/onboarding/onboarding-journey';
import { answerOnboardingPrompt, isOnboardingConversationId } from 'features/onboarding/onboarding-chat';
import { InvitationPayload } from 'features/group-chat/services/group-services';
import { generateAndSaveAgentAvatar } from 'pages/agents/utils/agent-avatar';
import {
  DEFAULT_MAIN_CHAT_AGENT_ID,
  isCatalogueAgentId,
  isDefaultMainChatAgentId,
} from 'utils/constants/chat-constants';
import { useNotificationListener } from 'hooks/use-notification-listener';
// import { v4 as uuidv4 } from 'uuid';
import { useGetConversationById } from 'hooks/use-conversation';
import { IamData } from '/features/iam/services/user-service';
import { ChatGreeting } from 'features/chat/components/chat-greeting';
import { CreateAgentSuggestions } from 'features/chat/components/create-agent-suggestions';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
// import { useCollaborationContext } from 'state/collaboration-websocket-middleware';
import { GroupChatMessages } from 'features/chat/components/GroupChatMessages';
import { useCollaborationSocketStore } from 'state/store/collaboration/use-collaboration-store';
// import { ConversationEvent } from 'services/conversation-services';
// import { IamData } from '/features/iam/services/user-service';

// import { useNotificationStore } from 'state/store/common/useNotificationStore';
import { useSearchParams } from 'react-router-dom';
import { claimBuddyConversation } from 'config/buddy/conversations';
import { getConversationById } from 'services/conversation-services';
import {
  isLlmCatalogueReady,
  isSmartChipUnsaved,
  isSyntheticAutoModel,
  resolveAgentLandingModelId,
  resolveDropdownModelId,
  resolveConversationModelName,
} from 'features/chat/utils/resolve-conversation-model';
// import { useListCustomGpts, useGetCustomGpt } from 'features/chat/hooks/use-custom-gpt';

export enum ButtonTypes {
  BUTTON,
  ICON,
}

export enum Alignment {
  LEFT,
  RIGHT,
}

export interface ChatInputButton {
  id: ButtonIds;
  label?: string;
  icon: React.ReactNode;
  onClick: () => void;
  type: ButtonTypes;
  alignment: Alignment;
  isDisabled?: boolean;
  isSelected?: boolean;
}

/**
 * Overflow of the chat page root. Desktop dashboards pin the canvas to the app column, so the
 * page must not clip it. Phones show no in-flow panel (the preview is a sheet), so they keep
 * the normal chat overflow.
 */
function chatPageOverflowClass(showDashboardCanvas: boolean, isEmptyChatLanding: boolean) {
  // Full class names, so Tailwind's source scan generates them.
  if (!showDashboardCanvas) return isEmptyChatLanding ? 'overflow-y-auto' : 'overflow-hidden';
  return isEmptyChatLanding
    ? 'max-md:overflow-y-auto md:overflow-visible'
    : 'max-md:overflow-hidden md:overflow-visible';
}

export default function ChatPage() {
  const [scrollButtonPortalHost, setScrollButtonPortalHost] = useState<HTMLDivElement | null>(null);
  const hasParticipantsArray = useChatStore((state) => state.hasParticipantsArray);
  const currentUser = useCommonStore((state) => state.user);
  const handleGroupChatNotification = useChatStore((state) => state.handleGroupChatNotification);
  const handleStreamingMessageBatch = useChatStore((state) => state.handleStreamingMessageBatch);
  const replyMessage = useChatStore((state) => state.replyMessage);
  // const lastProcessedCollaborationMessageRef = useRef<string | null>(null);
  // const fetchSidebarConversations = useChatStore((state) => state.fetchSidebarConversations);
  const temporaryConversation = useChatStore((state) => state.temporaryConversation);
  const replaceConversationId = useChatStore((state) => state.replaceConversationId);
  const sendGroupChatStream = useChatStore((state) => state.sendGroupChatStream);
  // const handleStreamingQueue = useChatStore((state) => state.handleStreamingQueue);
  const inviteUsersMutation = useInviteUsers();

  const inviteAssistantMutation = useInviteAssistant();
  const setGroupChat = useChatStore((state) => state.setGroupChat);

  // console.log('currentUser in chat page', currentUser);
  const { t } = useTranslation();
  const location = useLocation();
  // const [inputButtons, setInputButtons] = useState<ChatInputButton[]>([]);
  const [isFeatureSelected, setIsFeatureSelected] = useState<Record<ButtonIds, boolean>>(() => {
    const preservedFeatures = location.state?.preservedFeatures;
    return preservedFeatures || initialFeaturesState;
  });
  const otherMessages = useCollaborationSocketStore((state) => state.otherMessages);
  console.log('otherMessages in chat page', otherMessages)
  const connectionStatus = useCollaborationSocketStore((state) => state.connectionStatus);
  const streamingMessageBatch = useCollaborationSocketStore((state) => state.streamingMessageBatch);

  const [selectedSources, setSelectedSources] = useState<string[]>(() => {
    const preservedSources = location.state?.preservedSources;
    return preservedSources || [];
  });
  const [selectedFolderIdsBySource, setSelectedFolderIdsBySource] = useState<Record<string, string[]>>(() => {
    const preserved = location.state?.preservedFolderIdsBySource;
    return preserved || {};
  });
  const [selectedTools, setSelectedTools] = useState<string[]>(() => {
    const preservedTools = location.state?.preservedTools;
    return preservedTools || [];
  });

  const [isSourcesToolActive, setIsSourcesToolActive] = useState<boolean>(() => {
    const preservedSourcesToolActive = location.state?.preservedSourcesToolActive;
    const preservedSources = location.state?.preservedSources;
    // If we have preserved sources tool active state, use it; otherwise infer from sources length
    return preservedSourcesToolActive !== undefined
      ? preservedSourcesToolActive
      : preservedSources && preservedSources.length > 0;
  });

  const [chatInputHeight, setChatInputHeight] = useState(186);
  const [isPromptFieldFocused, setIsPromptFieldFocused] = useState(false);
  const [keepEmptyLandingDocked, setKeepEmptyLandingDocked] = useState(false);
  const messagesScrollContainerRef = useRef<HTMLDivElement>(null);
  // const [invitedUsers, setInvitedUsers] = useState<IamData[]>([]);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [hasShownWelcome, setHasShownWelcome] = useState(false);
  const [greetingMessage, setGreetingMessage] = useState<string>(''); // Default height
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showPaymentGracePeriodModal, setShowPaymentGracePeriodModal] = useState(false);
  const [showNameRequiredModal, setShowNameRequiredModal] = useState(false);
  const hasShownNameRequiredModalRef = useRef(false);

  const [paymentGracePeriodDays, setPaymentGracePeriodDays] = useState(0);
  const { conversationId: conversationIdFromParams } = useParams<ChatPageParams>();
  const viewedAssistantFile = useAssistantFileViewerStore((state) => state.file);
  const dashboardHtmlFile = useAssistantFileViewerStore((state) => state.dashboardHtmlFile);
  const isAssistantFileExpanded = useAssistantFileViewerStore((state) => state.isExpanded);
  const closeAssistantFile = useAssistantFileViewerStore((state) => state.closeFile);
  const closeDashboardHtml = useAssistantFileViewerStore((state) => state.closeDashboardHtml);
  const isDashboardCanvasDismissed = useAssistantFileViewerStore(
    (state) => state.isDashboardCanvasDismissed
  );
  const setDashboardCanvasDismissed = useAssistantFileViewerStore(
    (state) => state.setDashboardCanvasDismissed
  );
  const setDashboardConversation = useAssistantFileViewerStore(
    (state) => state.setDashboardConversation
  );
  const liveDashboardConversationId = useAssistantFileViewerStore(
    (state) => state.liveDashboardConversationId
  );

  useLayoutEffect(() => {
    closeAssistantFile();
    closeDashboardHtml();
    setDashboardCanvasDismissed(false);
  }, [
    conversationIdFromParams,
    closeAssistantFile,
    closeDashboardHtml,
    setDashboardCanvasDismissed,
  ]);

  // console.log('conversationIdFromParams', conversationIdFromParams, "projectIdFromParams", projectIdFromParams)
  const activeConversationId = useChatStore((state) => state.activeConversationId);
  // const setActiveConversationId = useChatStore((state) => state.setActiveConversationId);

  const conversations = useChatStore((state) => state.conversations);
  const setDisableNewChat = useChatStore((state) => state.setDisableNewChat);

  // // Clear activeConversationId when navigating to /chat without an ID
  // useEffect(() => {
  //   if (!conversationIdFromParams && activeConversationId !== null) {
  //     setActiveConversationId(null);
  //   }
  // }, [conversationIdFromParams, activeConversationId, setActiveConversationId]);

  const sendMessageToStore = useChatStore((state) => state.sendMessage);
  const isLoadingParticipants = useChatStore((state) => state.isLoadingParticipants);
  // console.log('isLoadingParticipants in chat page', isLoadingParticipants)
  const hasParticipants = useChatStore((state) => state.hasParticipant);
  const isGroupChat = hasParticipantsArray.length > 1;
  // Onboarding shows its own tab above the input (OnboardingChatPanel).
  const { isOnboarding, step, answers, setStep } = useOnboardingJourney();
  // An onboarding answer whose check failed for a technical reason goes back into the composer.
  const [onboardingRetryDraft, setOnboardingRetryDraft] = useState<{
    text: string;
    version: number;
  } | null>(null);
  useEffect(() => {
    setOnboardingRetryDraft(null);
  }, [conversationIdFromParams]);
  const updateConversationTitleInStore = useChatStore((state) => state.updateConversationTitleInStore);

  const currentConversation = activeConversationId ? conversations[activeConversationId] : null;
  console.log('currentConversation in chat page', currentConversation)
  const conversationFromUrl = conversationIdFromParams
    ? conversations[conversationIdFromParams]
    : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const isIncognitoChat = searchParams.get('incognito-chat') === 'true';
  // `?dashboard=new` only starts a dashboard chat; incognito chats are never dashboards.
  const isNewDashboardChat =
    !conversationIdFromParams && !isIncognitoChat && searchParams.get('dashboard') === 'new';
  // sendMessage can run from an older render's closure; read the latest value at send time.
  const isNewDashboardChatRef = useRef(isNewDashboardChat);
  isNewDashboardChatRef.current = isNewDashboardChat;
  const isLocalOnboardingChat = isOnboardingConversationId(conversationIdFromParams);
  const {
    refetch: refetchConversation,
    data: persistedConversation,
    isError: isConversationQueryError,
    error: conversationQueryError,
    isFetching: isConversationQueryFetching,
  } = useGetConversationById(
    isLocalOnboardingChat ? '' : (conversationIdFromParams as string),
    isIncognitoChat
  );

  // Once the conversation exists, its saved flag decides. Navigation state only covers the
  // moment before the conversation loads, so the layout does not flash to a normal chat.
  const savedDashboardMode =
    persistedConversation?.conversationId === conversationIdFromParams
      ? Boolean(persistedConversation?.isDashboard)
      : undefined;
  const dashboardNavigationHint =
    (location.state as { dashboard?: boolean } | null)?.dashboard === true && !isIncognitoChat;
  // A dashboard saved on this chat's live turn switches the layout before the saved
  // flag is read again.
  const hasLiveDashboard =
    !isIncognitoChat &&
    liveDashboardConversationId != null &&
    liveDashboardConversationId === (conversationIdFromParams ?? activeConversationId);
  const isDashboardConversation =
    (conversationIdFromParams
      ? (savedDashboardMode ?? dashboardNavigationHint)
      : isNewDashboardChat) || hasLiveDashboard;
  const showDashboardHtml =
    isDashboardConversation &&
    !isDashboardCanvasDismissed &&
    dashboardHtmlFile != null &&
    isHtmlAttachment(dashboardHtmlFile.fileName, dashboardHtmlFile.extension);
  const showDashboardCanvas =
    isDashboardConversation && !viewedAssistantFile && !isDashboardCanvasDismissed;

  // Message components read the mode from the viewer store rather than from the URL.
  useEffect(() => {
    setDashboardConversation(isDashboardConversation);
  }, [isDashboardConversation, setDashboardConversation]);
  useEffect(() => () => setDashboardConversation(false), [setDashboardConversation]);

  const conversationAccessError = useMemo(() => {
    if (!conversationIdFromParams || isIncognitoChat || isLocalOnboardingChat) return null;

    const hasLocalMessages = (conversationFromUrl?.messages?.length ?? 0) > 0;
    const isReady = !conversationFromUrl?.isLoading && !isLoadingParticipants;

    if (conversationFromUrl?.error && isReady) {
      const error = conversationFromUrl.error;
      if (shouldShowConversationUnavailable(error, hasLocalMessages)) {
        return error;
      }
    }

    if (
      isConversationQueryError &&
      conversationQueryError &&
      !isConversationQueryFetching &&
      !isLoadingParticipants &&
      !hasLocalMessages
    ) {
      const error = getConversationAccessErrorMessage(conversationQueryError);
      if (shouldShowConversationUnavailable(error, false)) {
        return error;
      }
    }

    return null;
  }, [
    conversationIdFromParams,
    isIncognitoChat,
    conversationFromUrl?.error,
    conversationFromUrl?.isLoading,
    conversationFromUrl?.messages?.length,
    isLoadingParticipants,
    isConversationQueryError,
    conversationQueryError,
    isConversationQueryFetching,
    isLocalOnboardingChat,
  ]);


  const cachedUsers: IamData[] = useMemo(() => {
    const unique = new Map<string, IamData>();
    const participants = currentConversation?.participants ?? [];
    const removedParticipants = currentConversation?.removedParticipants ?? [];

    [...participants, ...removedParticipants].forEach((u) => {
      if (u?.itemId && !unique.has(u.itemId)) {
        unique.set(u.itemId, u);
      }
    });

    return Array.from(unique.values());
  }, [currentConversation?.participants, currentConversation?.removedParticipants]);

  const streamingAssistantMessageId = currentConversation?.streamingAssistantMessageId;
  const messages = currentConversation?.messages ?? [];

  // Kill in-flight smooth scroll when leaving a conversation — otherwise the scroll
  // container animates upward as messages unmount (the "flying up" glitch on New Chat).
  useLayoutEffect(() => {
    if (messages.length > 0) return;
    const el = messagesScrollContainerRef.current;
    if (el) el.scrollTop = 0;
  }, [messages.length]);

  const isLoading: boolean = currentConversation?.isLoading ?? false;
  const isCurrentMessageLoading: boolean = currentConversation?.isCurrentMessageLoading ?? false;
  const isCancelOnGoing: boolean = currentConversation?.isCancelOnGoing ?? false;
  const isCurrentResponseGenerating: boolean =
    currentConversation?.isCurrentResponseGenerating ?? false;
  const canCancelGeneration = Boolean(currentConversation?.canCancelGeneration);
  // Once the turn that saved a dashboard is over, read the conversation again: the
  // backend has marked it a dashboard conversation (the saved flag then decides).
  const needsDashboardRefetch =
    hasLiveDashboard &&
    Boolean(conversationIdFromParams) &&
    !isLocalOnboardingChat &&
    !isCurrentResponseGenerating &&
    persistedConversation?.isDashboard !== true;
  useEffect(() => {
    if (needsDashboardRefetch) refetchConversation();
  }, [needsDashboardRefetch, refetchConversation]);
  // console.log('isCurrentResponseGenerating', isCurrentResponseGenerating)
  const { toast } = useToast();
  const setSelectedModel = useModelStore.getState().setSelectedModel;
  const selectedModel = useModelStore((state) => state.selectedModel);
  // const userChangedModel = useModelStore((state) => state.userChangedModel);
  const isUserChangedModel = useModelStore((state) => state.isUserChangedModel);
  const models = useModelStore((state) => state.models);
  useGetLlmModel();
  const setActiveConversationModelName = useChatStore(
    (state) => state.setActiveConversationModelName
  );

  const { configurationSettings } = useConfig();
  const user = useCommonStore((state) => state.user);
  const userId = user?.id;

  // ---- Agent selection (unified agent chat) ----
  // Conversation-derived once a conversation exists (agent participant is locked for
  // life); on a fresh unsent chat the ?agentId= search param holds the draft selection.
  const agentIdFromUrl = searchParams.get('agentId');
  const isCreateAgentMode = searchParams.get('createAgent') === 'true';
  const navigationDraft =
    (location.state as { agentDraft?: string; prompt?: string } | null)?.agentDraft ??
    (location.state as { agentDraft?: string; prompt?: string } | null)?.prompt ??
    undefined;

  // The draft is a one-time prefill. History keeps navigation state across reloads and
  // Back/Forward, so drop it from the browser entry (React Router stores it as `usr`)
  // without re-rendering, or the starter prompt reappears in the composer later.
  useEffect(() => {
    const state = window.history.state;
    if (!state?.usr?.agentDraft && !state?.usr?.prompt) return;
    const { agentDraft: _agentDraft, prompt: _prompt, ...rest } = state.usr;
    window.history.replaceState({ ...state, usr: rest }, '');
  }, [location.key]);

  useEffect(() => {
    if (!isCreateAgentMode) return;
    if (!searchParams.has('prompt') && !searchParams.has('agentId')) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('prompt');
        next.delete('agentId');
        next.set('createAgent', 'true');
        return next;
      },
      { replace: true },
    );
  }, [isCreateAgentMode, searchParams, setSearchParams]);
  const storeConversationAgentId = useChatStore((state) =>
    conversationIdFromParams
      ? state.conversations[conversationIdFromParams]?.agentId
      : undefined,
  );

  const setSelectedAgentIdStore = useAgentSelectionStore((state) => state.setSelectedAgentId);
  const hasUserSelection = useAgentSelectionStore((state) => state.hasUserSelection);
  const getSelectedAgentId = useAgentSelectionStore((state) => state.getSelectedAgentId);
  const clearUserSelection = useAgentSelectionStore((state) => state.clearUserSelection);

  const projectKey = configurationSettings?.blocksKey || '';
  const isSelectionStored = userId ? hasUserSelection(userId) : false;
  const storedAgentId = userId ? getSelectedAgentId(userId) : undefined;
  const isStoredSelectionCurrentUrl = !!agentIdFromUrl && storedAgentId === agentIdFromUrl;
  const hasAlternativeStoredSelection =
    isSelectionStored && !isStoredSelectionCurrentUrl && !isCreateAgentMode;

  // Bare /chat brings back the agent the user last picked (stored per user in localStorage).
  const agentIdToRestore =
    !conversationIdFromParams && !agentIdFromUrl && !isCreateAgentMode ? storedAgentId : null;

  // Fresh /chat (no conversation, no ?agentId=) falls back to Buddy when nothing is stored.
  const shouldLookupBuddy = !conversationIdFromParams && !agentIdFromUrl;
  const { data: agentsLookupData, isLoading: isAgentsLookupLoading } = useGetAgents(
    {
      limit: 50,
      offset: 0,
      project_key: projectKey,
    },
    { enabled: !!projectKey && shouldLookupBuddy, scope: 'agent-selector' }
  );

  const buddyAgent = useMemo(() => {
    if (!shouldLookupBuddy) return null;
    return findBuddyAgent(agentsLookupData?.agents);
  }, [shouldLookupBuddy, agentsLookupData?.agents]);

  const userChoseNoAgent = isSelectionStored && storedAgentId === null;

  // Preferred agent on fresh chat:
  // 1. Explicit URL parameter (user picked an agent or opened an agent link)
  // 2. Stored choice — an agent ID, or an explicit "no agent"
  // 3. Default "Buddy" agent
  let selectedAgentId: string | null = agentIdFromUrl;
  if (conversationIdFromParams) {
    // Incognito skips conversation GET, so fall back to the in-memory / URL agent id.
    selectedAgentId =
      conversationFromUrl?.agentId ?? storeConversationAgentId ?? agentIdFromUrl ?? null;
  } else if (agentIdToRestore) {
    selectedAgentId = agentIdToRestore;
  } else if (!agentIdFromUrl && !userChoseNoAgent) {
    selectedAgentId = buddyAgent?.id ?? null;
  }

  // Agents that already 404'd. Restoring one would delete the URL, then write it back, and
  // every pass refetches the page (and toasts again).
  const unavailableAgentIdsRef = useRef<Set<string>>(new Set());
  const locationStateRef = useRef(location.state);
  locationStateRef.current = location.state;

  // Put the restored agent back in the URL so the availability check below also covers
  // a stored agent that was deleted or unshared since it was picked. Keep location.state:
  // the feature-reset effect below reads `from: 'project'` from it.
  useEffect(() => {
    if (!agentIdToRestore) return;
    if (unavailableAgentIdsRef.current.has(agentIdToRestore)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('agentId', agentIdToRestore);
        return next;
      },
      { replace: true, state: location.state },
    );
  }, [agentIdToRestore, location.state, setSearchParams]);

  const {
    data: selectedAgentData,
    isLoading: isAgentDetailLoading,
    isError: isAgentDetailError,
    error: agentDetailError,
  } = useGetAgentById({ id: selectedAgentId ?? '' }, { enabled: !!selectedAgentId });

  // If user navigated with an explicit agentId in URL on a fresh chat, only record it in
  // storage after verifying it is available to the current user.
  useEffect(() => {
    if (conversationIdFromParams) return;
    if (
      userId &&
      agentIdFromUrl &&
      selectedAgentData?.agent &&
      selectedAgentData.agent.id === agentIdFromUrl &&
      agentIdFromUrl !== storedAgentId
    ) {
      setSelectedAgentIdStore(userId, agentIdFromUrl);
    }
  }, [
    conversationIdFromParams,
    userId,
    agentIdFromUrl,
    selectedAgentData?.agent,
    storedAgentId,
    setSelectedAgentIdStore,
  ]);

  // If user lands with an agentId in URL that is unavailable/inaccessible for this user on fresh chat,
  // do not store it; instead clear any invalid stored selection, toast an alert, and redirect to
  // the user's previously set agentId or the default (Buddy) agent.
  // Only a 404 means the agent is gone. A 503/504 must not toast or rewrite the URL — that
  // rewrite clears ?agentId=, the restore effect writes it back, and the page fetches again.
  useEffect(() => {
    if (conversationIdFromParams) return;
    if (!agentIdFromUrl) return;
    if (!isAgentDetailError) return;
    if (!(agentDetailError instanceof HttpError) || agentDetailError.status !== 404) return;
    if (unavailableAgentIdsRef.current.has(agentIdFromUrl)) return;

    // If fallback depends on buddyAgent lookup, wait until agents query settles
    if (shouldLookupBuddy && isAgentsLookupLoading) return;

    unavailableAgentIdsRef.current.add(agentIdFromUrl);

    if (userId && storedAgentId === agentIdFromUrl) {
      clearUserSelection(userId);
    }

    const code = (agentDetailError.error?.detail as { code?: string } | undefined)?.code;
    toast({
      variant: 'destructive',
      title: t('AGENT_UNAVAILABLE_TITLE') || 'Agent unavailable',
      description:
        code === 'AGENT_NOT_FOUND'
          ? t('AGENT_NOT_FOUND_DESCRIPTION') || 'This agent no longer exists.'
          : t('AGENT_UNAVAILABLE_DESCRIPTION') ||
            'This agent is no longer available to you. Its visibility or sharing may have changed.',
    });

    const candidate = hasAlternativeStoredSelection
      ? (storedAgentId ?? null)
      : (buddyAgent?.id ?? null);
    const targetAgentId =
      candidate && !unavailableAgentIdsRef.current.has(candidate) ? candidate : null;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (targetAgentId) {
          next.set('agentId', targetAgentId);
        } else {
          next.delete('agentId');
        }
        return next;
      },
      { replace: true },
    );
  }, [
    conversationIdFromParams,
    agentIdFromUrl,
    isAgentDetailError,
    agentDetailError,
    shouldLookupBuddy,
    isAgentsLookupLoading,
    hasAlternativeStoredSelection,
    storedAgentId,
    buddyAgent?.id,
    userId,
    clearUserSelection,
    setSearchParams,
    t,
    toast,
  ]);

  const setAgentAvailability = useChatStore((state) => state.setAgentAvailability);
  const agentUnavailable = useChatStore((state) =>
    conversationIdFromParams
      ? !!state.conversations[conversationIdFromParams]?.agentUnavailable
      : false,
  );

  // ---- Voice call ----
  // Direct agent chats only: the room holds user + agent, and a fresh voice call creates a
  // persisted conversation (which incognito must not do).
  const { startCall: startVoiceCall, endCall: endVoiceCall } = useVoiceCall();
  useEndVoiceCallOnChatChange(conversationIdFromParams, agentIdFromUrl, endVoiceCall);
  const voiceCallStatus = useVoiceCallStore((state) => state.status);
  const voiceCallUnavailable = useVoiceCallStore((state) => state.unavailable);
  const canStartVoiceCall =
    isVoiceCallEnabled &&
    !isGroupChat &&
    !hasParticipants &&
    !isIncognitoChat &&
    !isOnboarding &&
    !agentUnavailable &&
    !voiceCallUnavailable;
  const handleStartVoiceCall = useCallback(() => {
    void startVoiceCall({
      agentId: selectedAgentId ?? DEFAULT_MAIN_CHAT_AGENT_ID,
      conversationId: conversationIdFromParams ?? undefined,
    });
  }, [startVoiceCall, selectedAgentId, conversationIdFromParams]);
  // Load-path half of the AGENT_UNAVAILABLE contract: a 404 with code
  // AGENT_UNAVAILABLE/AGENT_NOT_FOUND blocks the composer; a later successful
  // refetch (window focus, reopen) clears the block without a page reload.
  const toastedUnavailableConversationRef = useRef<string | null>(null);
  useEffect(() => {
    if (!conversationIdFromParams) return;
    if (selectedAgentData?.agent) {
      toastedUnavailableConversationRef.current = null;
      setAgentAvailability(conversationIdFromParams, false);
      return;
    }
    if (isAgentDetailError && agentDetailError instanceof HttpError && agentDetailError.status === 404) {
      setAgentAvailability(conversationIdFromParams, true);
      if (toastedUnavailableConversationRef.current === conversationIdFromParams) return;
      toastedUnavailableConversationRef.current = conversationIdFromParams;
      const code = (agentDetailError.error?.detail as { code?: string } | undefined)?.code;
      toast({
        variant: 'destructive',
        title: t('AGENT_UNAVAILABLE_TITLE') || 'Agent unavailable',
        description:
          code === 'AGENT_NOT_FOUND'
            ? t('AGENT_NOT_FOUND_DESCRIPTION') || 'This agent no longer exists.'
            : t('AGENT_UNAVAILABLE_DESCRIPTION') ||
            'This agent is no longer available to you. Its visibility or sharing may have changed.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conversationIdFromParams,
    selectedAgentData,
    isAgentDetailError,
    agentDetailError,
    setAgentAvailability,
  ]);
  const selectedAgentLlm = selectedAgentData?.agent?.llm_config;
  const agentModelLoading = !!selectedAgentId && !selectedAgentData && !isAgentDetailError;
  const agentUploadModel = useMemo(() => {
    if (!selectedAgentLlm) return null;
    const { model_id, model_name } = selectedAgentLlm;
    const normalizedName = model_name?.toLowerCase();
    return (
      models.find(
        (m) =>
          (model_id && m.id === model_id) ||
          (model_name && (m.id === model_name || m.name === model_name)) ||
          (normalizedName &&
            (m.id.toLowerCase() === normalizedName || m.name.toLowerCase() === normalizedName)),
      ) ?? null
    );
  }, [selectedAgentLlm, models]);
  const agentModelLabel = agentUploadModel?.name ?? selectedAgentLlm?.model_name ?? null;

  const prevAgentIdRef = useRef<string | null | undefined>(undefined);
  const initializedAgentModelRef = useRef<string | null>(null);
  const prevConversationIdForAgentModelRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const leftExplicitAgentChat =
      !!prevConversationIdForAgentModelRef.current &&
      !conversationIdFromParams &&
      !!agentIdFromUrl;
    prevConversationIdForAgentModelRef.current = conversationIdFromParams;

    if (leftExplicitAgentChat) {
      useModelStore.getState().userChangedModel(false);
    }

    if (prevAgentIdRef.current !== selectedAgentId) {
      prevAgentIdRef.current = selectedAgentId;
      initializedAgentModelRef.current = null;
      useModelStore.getState().userChangedModel(false);
      if (!selectedAgentId) {
        const defaultModel =
          models.find((m) => m.isDefaultModel) ?? models.find((m) => m.id === 'auto');
        if (defaultModel) {
          setSelectedModel(defaultModel.id);
        }
      }
    }

    const landingAgentId = conversationIdFromParams
      ? agentIdFromUrl
      : agentIdFromUrl || selectedAgentId;
    // The landing was already initialized for this agent during the conversation.
    // If the chip is not that agent's model yet, apply it once. Skipping the
    // Smart reset avoids a second width change after the page has painted.
    const { selectedModel: currentModel, isUserChangedModel: userPickedModel } =
      useModelStore.getState();
    if (
      landingAgentId &&
      !conversationIdFromParams &&
      !userPickedModel &&
      agentUploadModel?.id &&
      currentModel?.id !== agentUploadModel.id
    ) {
      initializedAgentModelRef.current = null;
    }

    const landingModel = resolveAgentLandingModelId({
      agentId: landingAgentId,
      agentModelId: agentUploadModel?.id ?? null,
      hasConversation: !!conversationIdFromParams,
      leftConversation: leftExplicitAgentChat,
      initializedAgentId: initializedAgentModelRef.current,
      isUserChangedModel: userPickedModel,
    });
    initializedAgentModelRef.current = landingModel.initializedAgentId;
    if (landingModel.modelId) {
      setSelectedModel(landingModel.modelId);
    }
  }, [
    selectedAgentId,
    agentIdFromUrl,
    agentUploadModel,
    conversationIdFromParams,
    isUserChangedModel,
    models,
    setSelectedModel,
  ]);

  const handleAgentChange = (agentId: string | null, draftText: string) => {
    if (agentId === (selectedAgentId ?? null)) return;
    if (userId) {
      setSelectedAgentIdStore(userId, agentId);
    }
    if (conversationIdFromParams || activeConversationId) {
      // Changing who answers opens a fresh chat; the typed draft rides along. The route
      // change remounts the page, but the store must drop the old conversation itself.
      // (Not setNewChat — its reset effect would wipe the carried draft on mount.)
      useChatStore.getState().setActiveConversationId(null);
      useChatStore.getState().setReplyMessage(null);
      navigate(buildChatLandingPath({ agentId, isIncognito: isIncognitoChat }), {
        state: draftText ? { agentDraft: draftText } : undefined,
      });
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (agentId) next.set('agentId', agentId);
        else next.delete('agentId');
        return next;
      },
      { replace: true },
    );
  };

  // Picking an agent (from the selector or the landing "Agents" panel) closes the panel.
  useEffect(() => {
    if (!selectedAgentId) return;
    setIsFeatureSelected((prev) =>
      prev[ButtonIds.AGENTS] ? { ...prev, [ButtonIds.AGENTS]: false } : prev,
    );
  }, [selectedAgentId]);

  const {
    currentPlan,
    isLoading: isCurrentPlanLoading,
    setCurrentPlan,
    setLoading,
    setError,
  } = useSubscriptionStore();
  // Plans are per seat: the person needs a seat of their own, whatever the organization holds.
  const { hasPlan, isLoading: isMyPlanLoading } = useMyPlan();
  const queryClient = useQueryClient();
  const { currentLanguage } = useLanguageContext();
  const createAgentDraft = isCreateAgentMode
    ? navigationDraft?.trim() || defaultCreateAgentChatPrompt(currentLanguage)
    : navigationDraft;
  const setIsIncognitoChatFlag = useChatStore((state) => state.setIsIncognitoChat);

  /** Keep store flag aligned with URL so SSE title generation (!isIncognito) matches actual incognito mode after SPA navigations. */
  useEffect(() => {
    setIsIncognitoChatFlag(isIncognitoChat);
  }, [isIncognitoChat, setIsIncognitoChatFlag]);

  const handledPlatformResourceIdsRef = useRef<Set<string>>(new Set());

  const handlePlatformResourceEvent = useCallback(
    (rawData: any) => {
      let data = rawData;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return null;
        }
      }
      if (!data) return null;

      const kind = data?.kind;
      const action = data?.action;
      const resourceId = data?.resourceId;

      if (kind === 'agents' || !kind) {
        void queryClient.invalidateQueries({ queryKey: ['agents'] });
        if (resourceId) {
          void queryClient.invalidateQueries({ queryKey: ['agent', resourceId] });
          void queryClient.invalidateQueries({ queryKey: ['agents', resourceId] });
        }
      } else if (kind === 'tools') {
        void queryClient.invalidateQueries({ queryKey: ['tools'] });
        if (resourceId) {
          void queryClient.invalidateQueries({ queryKey: ['tools', resourceId] });
        }
      } else if (kind === 'skills') {
        void queryClient.invalidateQueries({ queryKey: ['skills'] });
        if (resourceId) {
          void queryClient.invalidateQueries({ queryKey: ['skills', resourceId] });
        }
      } else if (kind === 'kb_folders') {
        void queryClient.invalidateQueries({ queryKey: ['knowledge-base'] });
        void queryClient.invalidateQueries({ queryKey: ['folders'] });
      }

      if ((kind === 'agents' || !kind) && action !== 'deleted' && resourceId) {
        if (!handledPlatformResourceIdsRef.current.has(resourceId)) {
          handledPlatformResourceIdsRef.current.add(resourceId);
          if (configurationSettings?.blocksKey) {
            const agentName = (data.name || data.fields?.name || 'Agent').trim();
            const agentDescription = (data.fields?.description || data.description || '').trim();
            generateAndSaveAgentAvatar({
              agentId: resourceId,
              name: agentName,
              description: agentDescription,
              projectKey: configurationSettings.blocksKey,
              configurationSettings,
              queryClient,
            }).catch((err) => {
              console.error('Failed to generate agent avatar via DiceBear:', err);
            });
          }
          return resourceId;
        }
      }
      return null;
    },
    [queryClient, configurationSettings]
  );

  // const { data: assistantsData } = useListCustomGpts();
  // const { data: fetchedCustomGpt } = useGetCustomGpt(currentConversation?.customGptId || "");

  // const associatedAssistant = useMemo(() => {
  //   if (currentConversation?.customGptId) {
  //     return fetchedCustomGpt || assistantsData?.data?.find((a: any) => a.id === currentConversation.customGptId);
  //   }
  //   return null;
  // }, [currentConversation?.customGptId, assistantsData?.data, fetchedCustomGpt]);
  const associatedAssistant = null;

  useEffect(() => {
    // Onboarding conversations exist only locally, so the backend has nothing to invite the model into.
    if (isLocalOnboardingChat) return;
    if (selectedModel && conversationIdFromParams && isUserChangedModel) {
      changeSelectedModel();
    }
  }, [selectedModel, isUserChangedModel]);


  const fetchAndSetGroupChatParticipants = useChatStore(
    (state) => state.fetchAndSetGroupChatParticipants
  );

  useEffect(() => {
    if (location.pathname === '/chat' && location.state?.from !== 'project') {
      setIsFeatureSelected(initialFeaturesState);
      setSelectedSources([]);
      setIsSourcesToolActive(false);
    }
  }, [location.pathname, location.state]);


  // Bare /chat (no conversation id) should always show the new-chat landing.
  // Store state survives route changes, so clear stale conversations before paint —
  // useLayoutEffect avoids a visible frame of the old messages before they vanish.
  useLayoutEffect(() => {
    if (location.pathname !== '/chat' || conversationIdFromParams) return;

    const { activeConversationId, disableNewChat } = useChatStore.getState();
    if (!disableNewChat && activeConversationId !== null) {
      useChatStore.getState().setNewChat(true);
    }

    // Put the agent's model on the chip before paint. A model the user picked
    // on this landing stays put.
    const landingAgentId = agentIdFromUrl || selectedAgentId;
    const { selectedModel: currentModel, isUserChangedModel: userPickedModel } =
      useModelStore.getState();

    if (!landingAgentId) {
      if (!userPickedModel && currentModel && !isSyntheticAutoModel(currentModel.id)) {
        setSelectedModel('auto');
      }
      return;
    }

    if (userPickedModel) return;

    const agentModelId = agentUploadModel?.id ?? null;
    if (!agentModelId || currentModel?.id === agentModelId) {
      if (agentModelId) initializedAgentModelRef.current = landingAgentId;
      return;
    }

    initializedAgentModelRef.current = landingAgentId;
    setSelectedModel(agentModelId);
  }, [
    location.pathname,
    conversationIdFromParams,
    agentIdFromUrl,
    selectedAgentId,
    agentUploadModel,
    setSelectedModel,
  ]);


  const prevConversationIdFromParamsRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const paramsChanged =
      prevConversationIdFromParamsRef.current !== conversationIdFromParams;
    prevConversationIdFromParamsRef.current = conversationIdFromParams;

    if (!conversationIdFromParams) return;

    // Only sync when the URL param actually changes (navigation / first mount).
    // New Chat clears the store before navigation finishes, so for a moment we're
    // still on /chat/:id with activeConversationId=null — and ChatInput's reset
    // effect flips isNewChat back to false almost immediately, so we can't rely on
    // that flag. Re-activating the old conversation here remounts its messages and
    // auto-scrolls to bottom (visible jump when the user was scrolled up).
    if (!paramsChanged) return;

    if (useChatStore.getState().isNewChat) {
      useChatStore.setState({ isNewChat: false });
    }

    if (activeConversationId !== conversationIdFromParams) {
      useChatStore.getState().setActiveConversationId(conversationIdFromParams);
    }
  }, [conversationIdFromParams, activeConversationId]);
  useEffect(() => {
    const randomGreeting = getRandomGreeting(user?.firstName, currentLanguage);
    setGreetingMessage(randomGreeting);
  }, [user?.firstName, currentLanguage]);

  // console.log('streamingQueue in chat page', streamingQueue)

  useLayoutEffect(() => {
    if (!currentConversation || currentConversation.isLoading) {
      return;
    }

    const modelIds = models.map((model) => model.id);
    const fromParticipants = resolveConversationModelName(
      currentConversation.invitationParticipantIds,
      modelIds
    );

    const isExplicitConversationModel =
      currentConversation.modelName &&
      currentConversation.modelName !== 'auto' &&
      modelIds.includes(currentConversation.modelName);

    const fallbackModelName = isExplicitConversationModel
      ? currentConversation.modelName
      : selectedAgentId
      ? (agentUploadModel?.id ?? selectedAgentLlm?.model_id ?? selectedAgentLlm?.model_name)
      : currentConversation.modelName;

    const nextModelId = resolveDropdownModelId({
      conversationModelName: fromParticipants || fallbackModelName,
      invitationParticipantIds: currentConversation.invitationParticipantIds,
      modelIds,
      catalogueReady: isLlmCatalogueReady(modelIds),
    });

    if (!nextModelId) {
      return;
    }

    const isSmartUnsaved =
      !!conversationIdFromParams &&
      !isCatalogueAgentId(selectedAgentId) &&
      isSmartChipUnsaved(nextModelId, currentConversation.invitationParticipantIds);

    // modelName already reads "auto" for such a chat, so Smart must also be written into the
    // participant list here, or every re-render would find it unsaved and invite again.
    if (
      currentConversation.invitationParticipantIds?.length &&
      (nextModelId !== currentConversation.modelName || isSmartUnsaved)
    ) {
      setActiveConversationModelName(nextModelId);
    }

    if (selectedModel?.id !== nextModelId) {
      setSelectedModel(nextModelId);
    }

    // The chip falls back to Smart when the saved model left the list; save it so the
    // server stops running the removed model.
    if (isSmartUnsaved) {
      useModelStore.getState().userChangedModel(true);
    }
  }, [
    currentConversation,
    models,
    selectedAgentId,
    agentUploadModel?.id,
    selectedAgentLlm?.model_id,
    selectedAgentLlm?.model_name,
    selectedModel?.id,
    setActiveConversationModelName,
    setSelectedModel,
  ]);

  const prevIncognitoChatQueryRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (prevIncognitoChatQueryRef.current === undefined) {
      prevIncognitoChatQueryRef.current = isIncognitoChat;
      return;
    }
    const leftIncognito =
      prevIncognitoChatQueryRef.current === true && isIncognitoChat === false;
    prevIncognitoChatQueryRef.current = isIncognitoChat;

    if (!leftIncognito || conversationIdFromParams) return;

    const { models: modelList, setSelectedModel: applySelectedModelId, userChangedModel } =
      useModelStore.getState();
    const defaultModel =
      modelList.find((m) => m.isDefaultModel) ?? modelList.find((m) => m.id === 'auto');
    if (defaultModel) {
      applySelectedModelId(defaultModel.id);
    }
    userChangedModel(false);
  }, [isIncognitoChat, conversationIdFromParams]);

  useEffect(() => {
    if (
      conversationIdFromParams &&
      configurationSettings &&
      !isIncognitoChat &&
      !isOnboardingConversationId(conversationIdFromParams)
    ) {
      const existingMessageCount =
        useChatStore.getState().conversations[conversationIdFromParams]?.messages?.length ?? 0;

      fetchAndSetGroupChatParticipants(
        conversationIdFromParams as string,
        configurationSettings,
        false,
        existingMessageCount === 0,
      );
    }
  }, [conversationIdFromParams, configurationSettings, isIncognitoChat]);

  useEffect(() => {
    if (currentPlan?.status == 'payment_pending') {
      const validUntil = new Date(currentPlan?.currentPeriodEnd ?? '');
      const today = new Date();
      const diffTime = Math.abs(validUntil.getTime() - today.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setPaymentGracePeriodDays(diffDays);

      setShowPaymentGracePeriodModal(true);
    }
  }, [currentPlan]);

  // Check if user has first name and last name set up
  useEffect(() => {
    if (user && !hasShownNameRequiredModalRef.current && !showWelcomeDialog && !hasShownWelcome) {
      const hasFirstName = user.firstName && user.firstName.trim() !== '';
      const hasLastName = user.lastName && user.lastName.trim() !== '';

      // Show modal if user is missing first name or last name
      if (!hasFirstName || !hasLastName) {
        setShowNameRequiredModal(true);
        hasShownNameRequiredModalRef.current = true;
      }
    }
  }, [user]);

  useEffect(() => {
    if (!otherMessages || otherMessages.length === 0) return;

    const messagesToProcess = otherMessages;
    // Drain the queue immediately so a re-render during async work doesn't
    // make us re-process the same events.
    useCollaborationSocketStore.getState().clearOtherMessages();

    for (const lastOtherMessage of messagesToProcess) {
      const notificationType = lastOtherMessage?.type;
      const isCurrentConversation =
        conversationIdFromParams === lastOtherMessage?.response?.conversationId;

      try {
        switch (notificationType) {
          case 'PLATFORM_RESOURCE_CHANGED': {
            const rawData =
              lastOtherMessage?.data ||
              lastOtherMessage?.streamingResponse?.data;
            handlePlatformResourceEvent(rawData);
            break;
          }
          case 'CONVERSATION_CREATED':
            handleGroupChatNotification(
              lastOtherMessage,
              configurationSettings,
              notificationType,
            );
            break;
          case 'ADD_PARTICIPANT':
            if (claimBuddyConversation(lastOtherMessage?.response?.conversationId)) break;
            if (models?.length > 0) {
              const modelIds = models.map((m) => m.id);
              const participantIds =
                lastOtherMessage.userIdsByType ||
                (lastOtherMessage.actorId ? [lastOtherMessage.actorId] : []);
              const nonModelParticipants = participantIds?.filter(
                (id: string) =>
                  lastOtherMessage.participantType !== 'assistant' &&
                  !modelIds.includes(id) &&
                  !isDefaultMainChatAgentId(id)
              );

              const selectedModelId = participantIds?.find((id: string) =>
                modelIds.includes(id)
              );
              if (nonModelParticipants && nonModelParticipants.length > 0) {
                const fetchLatestConversationAndUpdate = async () => {
                  const freshData = await getConversationById(
                    lastOtherMessage?.response?.conversationId as string,
                    configurationSettings
                  );

                  const rawParticipants = freshData?.participants;
                  const participantList = Array.isArray(rawParticipants)
                    ? rawParticipants
                    : rawParticipants && typeof rawParticipants === 'object'
                      ? Object.values(rawParticipants)
                      : [];

                  const userParticipantCount = participantList.filter((p: any) => {
                    const pType = (
                      typeof p === 'string'
                        ? p
                        : p?.participantType || p?.type || ''
                    )
                      .toString()
                      .toLowerCase();
                    return pType === 'user';
                  }).length;

                  const isGroup = userParticipantCount > 1;

                  const existing = useChatStore
                    .getState()
                    .sidebarConversationsList.find(
                      (c) => c.ConversationId === lastOtherMessage?.response?.conversationId
                    );

                  const newSidebarEntry = {
                    ConversationId: lastOtherMessage?.response?.conversationId as string,
                    ConversationTitle:
                      freshData?.conversationTitle ?? existing?.ConversationTitle ?? '',
                    CreatedDate: existing?.CreatedDate ?? new Date().toISOString(),
                    LastUpdatedDate: new Date().toISOString(),
                    Conversations: existing?.Conversations ?? [],
                    IsGroup: isGroup,
                    TenantId: existing?.TenantId ?? '',
                    CreatedBy: existing?.CreatedBy ?? currentUser?.id ?? '',
                    ProjectId: existing?.ProjectId,
                    ShareToken: existing?.ShareToken,
                    CustomGptId: existing?.CustomGptId,
                    AgentId: existing?.AgentId,
                  };
                  useChatStore.getState().appendToSidebarConversationsList(newSidebarEntry);
                };
                fetchLatestConversationAndUpdate();
              }

              if (currentUser?.id && nonModelParticipants?.includes(currentUser.id)) {
                toast({
                  title:
                    t('ADDED_TO_GROUP_CHAT_TITLE') ||
                    'You have been added into a group chat',
                  description:
                    t('ADDED_TO_GROUP_CHAT_DESCRIPTION') ||
                    'You have been added into a group chat',
                  variant: 'success',
                  duration: 3000,
                });
              }
              if (nonModelParticipants && nonModelParticipants.length > 0 && isCurrentConversation) {
                fetchAndSetGroupChatParticipants(
                  conversationIdFromParams as string,
                  configurationSettings,
                  true
                );
                refetchConversation();

                const invitedBy = lastOtherMessage.actorId;

                // console.log('invitedBy', invitedBy)
                if (invitedBy) {
                  const userIds = {
                    invitedBy,
                    invitedTo: nonModelParticipants,
                  };

                  handleGroupChatNotification(
                    lastOtherMessage,
                    configurationSettings,
                    notificationType,
                    userIds
                  );
                }
              }
              if (
                selectedModelId &&
                selectedModel?.id !== selectedModelId &&
                conversationIdFromParams === lastOtherMessage?.response?.conversationId
              ) {
                // console.log('selectedModelId', selectedModelId)
                //   // Update conversation modelName
                useChatStore.setState((state) => ({
                  conversations: {
                    ...state.conversations,
                    [conversationIdFromParams as string]: {
                      ...state.conversations[conversationIdFromParams as string],
                      modelName: selectedModelId,
                    },
                  },
                }));
              }
            }
            break;

          case 'USER_ADDED_BY_TOKEN':
            if (claimBuddyConversation(lastOtherMessage?.response?.conversationId)) break;
            if (models?.length > 0) {
              const modelIds = models.map((m) => m.id);
              const nonModelParticipants = lastOtherMessage.userIdsByType?.filter(
                (id: string) =>
                  lastOtherMessage.participantType !== 'assistant' &&
                  !modelIds.includes(id) &&
                  !isDefaultMainChatAgentId(id)
              );

              const selectedModelId = lastOtherMessage.userIdsByType?.find((id: string) =>
                modelIds.includes(id)
              );
              if (nonModelParticipants && nonModelParticipants.length > 0) {
                const fetchLatestConversationAndUpdate = async () => {


                  const freshData = await getConversationById(lastOtherMessage?.response?.conversationId as string, configurationSettings);
                  const newSidebarEntry = {
                    ConversationId: lastOtherMessage?.response?.conversationId as string,
                    ConversationTitle: freshData?.conversationTitle ?? "",
                    CreatedDate: new Date().toISOString(),
                    LastUpdatedDate: new Date().toISOString(),
                    Conversations: [],
                    IsGroup: true,
                    TenantId: '',
                    CreatedBy: currentUser?.id ?? '',
                  };
                  useChatStore.getState().appendToSidebarConversationsList(newSidebarEntry);


                }
                fetchLatestConversationAndUpdate();
              }


              if (currentUser?.id && nonModelParticipants?.includes(currentUser.id)) {
                toast({
                  title:
                    t('JOINED_GROUP_CHAT_TITLE') ||
                    'You joined the group chat',
                  description:
                    t('JOINED_GROUP_CHAT_DESCRIPTION') ||
                    'You joined the group chat',
                  variant: 'success',
                  duration: 3000,
                });
              }
              if (nonModelParticipants && nonModelParticipants.length > 0 && isCurrentConversation) {
                fetchAndSetGroupChatParticipants(
                  conversationIdFromParams as string,
                  configurationSettings,
                  true
                );
                refetchConversation();

                const invitedBy = lastOtherMessage.actorId;

                if (invitedBy) {
                  const userIds = {
                    invitedBy,
                    invitedTo: nonModelParticipants,
                  };

                  handleGroupChatNotification(
                    lastOtherMessage,
                    configurationSettings,
                    notificationType,
                    userIds
                  );
                }
              }
              if (
                selectedModelId &&
                selectedModel?.id !== selectedModelId &&
                conversationIdFromParams === lastOtherMessage?.response?.conversationId
              ) {
                useChatStore.setState((state) => ({
                  conversations: {
                    ...state.conversations,
                    [conversationIdFromParams as string]: {
                      ...state.conversations[conversationIdFromParams as string],
                      modelName: selectedModelId,
                    },
                  },
                }));
              }
            }
            break;

          case 'CONVERSATION_TITLE_GENERATED':
          case 'CONVERSATION_TITLE_UPDATED':
            handleConversationNotification(notificationType, isCurrentConversation, lastOtherMessage);
            break;

          case 'REMOVE_PARTICIPANT':
            handleConversationNotification(notificationType, isCurrentConversation, lastOtherMessage);
            break;

          case 'CONVERSATION_DELETED':
            handleConversationNotification(notificationType, isCurrentConversation, lastOtherMessage);
            break;
          case 'TYPING_STATUS':
            break;

          default:
            break;
        }
      } catch (err) {
        console.error('Error handling collaboration message:', err);
      }
    }
  }, [otherMessages]);

  useEffect(() => {
    if (!configurationSettings || !streamingMessageBatch || streamingMessageBatch.length === 0)
      return;

    const processBatch = async () => {
      try {
        // Drain atomically from the live store rather than the render closure so
        // rapid streaming batches aren't dropped by a trailing clear (see the
        // collaboration store's drainStreamingBatch for details).
        const batch = useCollaborationSocketStore.getState().drainStreamingBatch();
        if (batch.length === 0) return;

        const userMessages: any[] = [];
        const streamingMessages: any[] = [];
        const processingStartedMessages: any[] = [];
        for (const message of batch) {
          if (!message) continue;

          if (
            message?.streamingResponse?.type === 'PLATFORM_RESOURCE_CHANGED' ||
            message?.type === 'PLATFORM_RESOURCE_CHANGED'
          ) {
            const rawData =
              message?.streamingResponse?.type === 'PLATFORM_RESOURCE_CHANGED'
                ? message?.streamingResponse?.data
                : message?.data;
            handlePlatformResourceEvent(rawData);
          }

          switch (message.type) {
            case 'USER_MESSAGE':
              if (message?.response?.message?.senderId) {
                userMessages.push(message);
              }
              break;

            case 'ASSISTANT_STREAMED_RESPONSE':
              if (message?.streamingResponse?.type === 'ASSISTANT_REQUEST_PROCESSING_STARTED') {
                processingStartedMessages.push(message);
              } else {
                streamingMessages.push(message);
              }
              break;

            case 'PLATFORM_RESOURCE_CHANGED':
              streamingMessages.push(message);
              break;

            default:
              break;
          }
        }

        for (const msg of userMessages) {
          await handleGroupChatNotification(msg, configurationSettings, msg.type);
        }

        for (const msg of processingStartedMessages) {
          await handleGroupChatNotification(msg, configurationSettings, msg.streamingResponse.type);
        }

        if (streamingMessages.length > 0) {
          handleStreamingMessageBatch(streamingMessages, configurationSettings);
        }
      } catch (err) {
        console.error('Error handling streaming message batch:', err);
      }
    };

    processBatch();
  }, [
    streamingMessageBatch,
    configurationSettings,
    handleGroupChatNotification,
    handleStreamingMessageBatch,
    handlePlatformResourceEvent,
  ]);

  useNotificationListener('blocks-notification', (event) => {
    const rawPayload = (event as { message?: { denormalizedPayload?: unknown } })?.message
      ?.denormalizedPayload;
    if (typeof rawPayload !== 'string') return;

    try {
      const payload = JSON.parse(rawPayload);

      if (!payload?.data?.length || typeof payload.data[0] !== 'string' || payload?.Message) return;

      const notificationData = JSON.parse(payload.data[0]);

      if (notificationData?.type === 'file_upload') {
        console.log('file_upload notification in chat page', notificationData);
      } else if (notificationData?.type === 'learned_skill') {
        const skillName = notificationData.display_name || notificationData.name;
        if (notificationData.action === 'created') {
          toast({
            variant: 'success',
            title: t('SUCCESS'),
            description: `${skillName} created`,
          });
        } else if (notificationData.action === 'updated') {
          toast({
            variant: 'success',
            title: t('SUCCESS'),
            description: `${skillName} updated`,
          });
        }
      }
    } catch (err) {
      console.error('Error parsing notification payload:', err);
    }
  });

  const changeSelectedModel = async () => {
    const payload: InvitationPayload = {
      invitationType: 'ADD_USER',
      conversationId: conversationIdFromParams,
      assistantId: selectedModel?.id,
      ...(!isCatalogueAgentId(selectedAgentId) && {
        agentId: DEFAULT_MAIN_CHAT_AGENT_ID,
      }),
    };
    const response = await inviteUsersMutation.mutateAsync(payload);
    if (response?.conversationId) {
      useModelStore.getState().userChangedModel(false);
    }
  };

  const handleConversationNotification = async (
    type: string,
    isCurrentConversation: boolean,
    notificationData: any
  ) => {
    // if (isCurrentConversation) {
    //   await fetchAndSetGroupChatParticipants(conversationIdFromParams as string, configurationSettings, true);
    // }
    switch (type) {
      case 'CONVERSATION_DELETED': {
        const conversationId = notificationData?.response?.conversationId;

        // Remove conversation from state if it exists
        if (conversationId) {
          const state = useChatStore.getState();
          const remainingConversations = { ...state.conversations };
          delete remainingConversations[conversationId];
          useChatStore.setState({ conversations: remainingConversations });

          // Remove from sidebar conversations list
          useChatStore.getState().removeConversationFromSidebarById(conversationId);
          useChatStore.getState().removeConversationFromSidebarProjectListById(conversationId);
          removeAgentConversationFromCache(queryClient, conversationId);

          // Skip toast when this tab already deleted (and toasted) the conversation.
          if (!wasConversationDeletedLocally(conversationId)) {
            toast({
              variant: 'success',
              title: t('SUCCESS'),
              description: t('DELETE_SUCCESS_DESCRIPTION') || 'Conversation deleted successfully.',
              duration: 3000,
            });
          }
        }

        // If it's the current conversation and the current user was involved
        if (isCurrentConversation) {
          // const isUserInvolved = currentUser?.id &&
          //   notificationData.userIdsByType?.length > 0 &&
          //   notificationData.userIdsByType.includes(currentUser.id);

          // if (isUserInvolved) {

          // }

          useChatStore.getState().setNewChat(true);
          useChatStore.getState().setActiveConversationId(null);
          navigate('/chat');
        }

        break;
      }


      case 'REMOVE_PARTICIPANT': {
        const conversationId = notificationData?.response?.conversationId;
        const isCurrentUserRemoved =
          currentUser?.id && notificationData.userIdsByType?.includes(currentUser.id);

        // If current user is removed, remove conversation from sidebar
        if (isCurrentUserRemoved && conversationId) {
          // toast({
          //   title:
          //     t('REMOVE_PARTICIPANT_TITLE') ||
          //     'You have been removed from a group chat',
          //   description:
          //     t('REMOVE_PARTICIPANT_DESCRIPTION') ||
          //     'You have been removed from a group chat',
          //   variant: 'destructive',
          //   duration: 3000,
          // });

          // Remove conversation from state
          const state = useChatStore.getState();
          const remainingConversations = { ...state.conversations };
          delete remainingConversations[conversationId];
          useChatStore.setState({ conversations: remainingConversations });

          // Remove from sidebar conversations list
          useChatStore.getState().removeConversationFromSidebarById(conversationId);
          useChatStore.getState().removeConversationFromSidebarProjectListById(conversationId);
          removeAgentConversationFromCache(queryClient, conversationId);

          // If it's the current conversation, navigate away
          if (isCurrentConversation) {
            useChatStore.getState().setActiveConversationId(null);
            useChatStore.getState().setNewChat(true);
            navigate('/chat');
          }
        } else {
          // await fetchSidebarConversations(configurationSettings);
          const { data: freshData } = await refetchConversation();
          updateConversationTitleInStore(conversationId, freshData?.conversationTitle ?? "");
        }

        // If it's the current conversation, handle the notification to show system message
        if (isCurrentConversation) {
          handleGroupChatNotification(
            notificationData,
            configurationSettings,
            'REMOVE_PARTICIPANT'
          );
        }

        break;
      }
      case 'CONVERSATION_TITLE_GENERATED': {
        const conversationId = notificationData?.response?.conversationId;
        if (!conversationId || !configurationSettings) break;
        if (claimBuddyConversation(conversationId)) break;
        const chatState = useChatStore.getState();
        const knownConversation =
          Boolean(chatState.conversations?.[conversationId]) ||
          (chatState.sidebarConversationsList ?? []).some(
            (item) => item.ConversationId === conversationId
          );
        if (!knownConversation) break;

        const freshData = await getConversationById(conversationId, configurationSettings);
        const generatedTitle = freshData?.conversationTitle ?? '';
        updateConversationTitleInStore(conversationId, generatedTitle);
        syncConversationRenameCaches(queryClient, conversationId, generatedTitle);
        break;
      }
      case 'CONVERSATION_TITLE_UPDATED': {
        const conversationId = notificationData?.response?.conversationId;
        if (!conversationId || !configurationSettings) break;
        if (claimBuddyConversation(conversationId)) break;
        const chatState = useChatStore.getState();
        const knownConversation =
          Boolean(chatState.conversations?.[conversationId]) ||
          (chatState.sidebarConversationsList ?? []).some(
            (item) => item.ConversationId === conversationId
          );
        if (!knownConversation) break;

        const freshData = await getConversationById(conversationId, configurationSettings);
        const updatedTitle = freshData?.conversationTitle ?? '';
        updateConversationTitleInStore(conversationId, updatedTitle, { isManual: true });
        syncConversationRenameCaches(queryClient, conversationId, updatedTitle);
        if (hasParticipants) {
          handleGroupChatNotification(
            notificationData,
            configurationSettings,
            'CONVERSATION_TITLE_UPDATED'
          );
        }
        break;
      }
    }
  };


  let streamingMessage = messages.find((message) => {
    if (message.role === 'assistant' && streamingAssistantMessageId) {
      return (
        message.recordId === streamingAssistantMessageId ||
        message.generations.some((generation) => generation.id === streamingAssistantMessageId)
      );
    }
    return false;
  });


  if (!streamingMessage && isCurrentResponseGenerating) {
    const assistantMessages = messages.filter((msg) => msg.role === 'assistant');
    if (assistantMessages.length > 0) {
      const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];
      const hasOngoingGeneration = lastAssistantMessage.generations.some(
        (gen) => gen.isThisGenerationOnGoing === true
      );
      const hasEmptyInFlightGeneration = lastAssistantMessage.generations.some(
        (gen) => !(gen.content?.trim())
      );
      if (hasOngoingGeneration || hasEmptyInFlightGeneration || !streamingAssistantMessageId) {
        streamingMessage = lastAssistantMessage;
      }
    }
  }


  // Auto-resume after a reactive MCP-service login (auth_required): reuse the same paused
  // turn (no duplicate user bubble) via the resume endpoint, replaying the turn's metadata.
  const lastSendArgsRef = useRef<
    [string, Attachment[], IntegrationSource[]?, Mention[]?, string[]?, MessageSkillMention[]?] | null
  >(null);
  const resumeCtxRef = useRef<{ isFeatureSelected?: Record<ButtonIds, boolean> }>({});
  resumeCtxRef.current = { isFeatureSelected };

  useEffect(() => {
    useMcpServiceChatStore.getState().setResume(async () => {
      const args = lastSendArgsRef.current;
      const sources = args?.[2];
      const tools = args?.[4];
      const skillIds = args?.[5]?.map((skill) => skill.id) ?? [];
      const features = resumeCtxRef.current?.isFeatureSelected;
      console.log('[MCP_RESUME_DEBUG] chat.tsx resume() invoked', { hasArgs: !!args });
      await useChatStore.getState().resumePendingAuthTurn(configurationSettings, {
        tool_ids: tools ?? [],
        skill_ids: skillIds,
        searchOnline: features?.[ButtonIds.SEARCH] ?? false,
        imageGeneration: features?.[ButtonIds.CREATE_IMAGE] ?? false,
        integrationSources: sources ?? [],
        content: args?.[0] ?? '',
      });
    });
    return () => useMcpServiceChatStore.getState().setResume(null);
  }, [configurationSettings]);

  const sendMessage = async (
    content: string,
    files: Attachment[],
    sources?: IntegrationSource[],
    mentions?: Mention[],
    tools?: string[],
    skillMentions?: MessageSkillMention[]
  ) => {
    lastSendArgsRef.current = [content, files, sources, mentions, tools, skillMentions];
    if (isOnboarding && conversationIdFromParams) {
      const result = await answerOnboardingPrompt(
        conversationIdFromParams,
        step ?? 1,
        Boolean(answers.onboardingCompleted && answers.onboardingTools?.trim()),
        content,
        setStep
      );
      // The answer couldn't be checked: put it back in the composer to send again.
      if (result === 'retry') {
        setOnboardingRetryDraft((previous) => ({
          text: content,
          version: (previous?.version ?? 0) + 1,
        }));
      }
      return;
    }
    const skillIds = skillMentions?.map((skill) => skill.id);
    const shouldKeepEmptyLandingDocked = isMobile && messages.length === 0;
    if (shouldKeepEmptyLandingDocked) {
      setKeepEmptyLandingDocked(true);
    }
    try {
    if (selectedModel?.id) {
      const selectedModelId = selectedModel.id;
      const alreadyExists = mentions?.some((mention) => mention.userId === selectedModelId);
      if (!alreadyExists) {
        const modelIdsSet = new Set(models?.map((model) => model.id));
        mentions = mentions?.map((mention) => {
          if (!modelIdsSet.has(mention.userId)) {
            return mention;
          }
          return {
            ...mention,
            userId: selectedModelId,
            user: {
              ...mention.user!,
              itemId: selectedModelId,
              email: selectedModelId,
              userName: selectedModelId
            }
          }
        })
      }
    }

    if (!conversationIdFromParams) {
      const createDashboard = isNewDashboardChatRef.current;
      const dashboardNavigation = createDashboard ? { state: { dashboard: true } } : undefined;
      // Agent chat: the agent (not a model) is the responder — invite it, send through
      // the store (which tags the conversation and nests it under the agent in the
      // sidebar), then open the new conversation.
      if (selectedAgentId) {
        const agentPayload: InvitationPayload = {
          invitationType: 'ADD_USER',
          agentId: selectedAgentId,
          assistantId: selectedModel?.id,
          isDashboard: createDashboard,
          isTemporary: isIncognitoChat ? true : false,
        };
        setGroupChat(false);
        const response = await runLockedNewChatInvitation(
          setDisableNewChat,
          () => inviteAssistantMutation.mutateAsync(agentPayload),
        );
        if (response?.conversationId) {
            const newConversationId = response.conversationId;
            trackEvent(EVENTS.NEW_CHAT_STARTED(newConversationId, InitialPromptType.TEXT));
            await sendMessageToStore(
              content,
              configurationSettings,
              newConversationId,
              files,
              isFeatureSelected,
              false,
              undefined,
              sources,
              false,
              undefined,
              mentions,
              undefined,
              isIncognitoChat ? true : false,
              undefined,
              tools,
              skillIds,
              selectedAgentId,
            );
            prependAgentConversationToCache(
              queryClient,
              selectedAgentId,
              configurationSettings?.blocksKey || '',
              newConversationId,
            );
            invalidateAgentConversationList(queryClient);
            navigate(
              buildChatConversationPath(newConversationId, {
                isIncognito: isIncognitoChat,
                agentId: selectedAgentId,
              }),
              dashboardNavigation,
            );
        }
        return;
      }

      // const { temporaryConversationId, recordId, assistantMessageId } = temporaryConversation(content, files, isFeatureSelected);
      const { temporaryConversationId, recordId } = temporaryConversation(
        content,
        files,
        isFeatureSelected
      );

      const payload: InvitationPayload = {
        invitationType: 'ADD_USER',
        agentId: DEFAULT_MAIN_CHAT_AGENT_ID,
        assistantId: selectedModel?.id,
        isDashboard: createDashboard,
        isTemporary: isIncognitoChat ? true : false,
      };

      setGroupChat(false);
      const response = await runLockedNewChatInvitation(
        setDisableNewChat,
        () => inviteAssistantMutation.mutateAsync(payload),
        () => useChatStore.getState().setNewChat(true),
      );
      if (response?.conversationId) {
        const newConversationId = response?.conversationId;

        // Replace the temporary conversationId with the real one
        replaceConversationId(temporaryConversationId, newConversationId, isIncognitoChat);

        trackEvent(EVENTS.NEW_CHAT_STARTED(newConversationId, InitialPromptType.TEXT));

        // if (response) {
        let mentionUserIds: string[];
        if (!hasParticipantsArray || hasParticipantsArray?.length <= 1) {
          mentionUserIds = [];
        } else if (mentions && mentions.length > 0) {
          const userIds = new Set(mentions.map((m) => m.userId));
          mentionUserIds = Array.from(userIds);
        } else {
          mentionUserIds = [DEFAULT_MAIN_CHAT_AGENT_ID];
        }
        const imageGeneration = isFeatureSelected?.[ButtonIds.CREATE_IMAGE] ?? false;
        const searchOnline = isFeatureSelected?.[ButtonIds.SEARCH] ?? false;
        const integrationSources = sources ? sources : [];

        await sendGroupChatStream(
          content,
          configurationSettings,
          newConversationId,
          files as unknown as FileMetadata[],
          replyMessage,
          mentionUserIds,
          false,
          undefined,
          imageGeneration,
          integrationSources,
          searchOnline,
          undefined,
          recordId,
          tools,
          skillIds
        );

        navigate(
          buildChatConversationPath(newConversationId, {
            isIncognito: isIncognitoChat,
          }),
          dashboardNavigation,
        );
      }
    } else {
      const parentIds = replyMessage?.generation ? [replyMessage?.generation.id] : [];
      sendMessageToStore(
        content,
        configurationSettings,
        undefined,
        files,
        isFeatureSelected,
        isGroupChat,
        parentIds,
        sources,
        false,
        undefined,
        mentions,
        undefined,
        isIncognitoChat ? true : false,
        undefined,
        tools,
        skillIds,
        selectedAgentId ?? undefined,
      );
    }
    } finally {
      if (shouldKeepEmptyLandingDocked) {
        setKeepEmptyLandingDocked(false);
      }
    }
  };

  // console.log('hasParticipants in chat page', hasParticipants, "isLoadingParticipants", isLoadingParticipants, "hasParticipantsArray", hasParticipantsArray)

  //  groupChatParticipants && Object.keys(groupChatParticipants).length > 0;

  // On WS reconnect, re-fetch the active conversation so anything that happened
  // during the disconnect gap (chunks delivered to other participants, title
  // changes, joins/leaves) is reconciled via REST instead of relying solely on
  // the BE's narrow in-flight replay buffer.
  const prevConnectionStatusRef = useRef(connectionStatus);
  useEffect(() => {
    const previous = prevConnectionStatusRef.current;
    prevConnectionStatusRef.current = connectionStatus;
    if (
      previous === 'DISCONNECTED' &&
      connectionStatus === 'CONNECTED' &&
      conversationIdFromParams &&
      configurationSettings &&
      !isOnboardingConversationId(conversationIdFromParams)
    ) {
      refetchConversation();
      fetchAndSetGroupChatParticipants(
        conversationIdFromParams as string,
        configurationSettings,
        true,
      );
    }
  }, [connectionStatus, conversationIdFromParams]);

  const inputButtons = useMemo<ChatInputButton[]>(
    () => [
      {
        id: ButtonIds.AGENTS,
        label: t('AGENTS', { defaultValue: 'Agents' }),
        icon: (
          <CreateAgentIcon width={20} height={20} strokeWidth={1.75} />
        ),
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.AGENTS]: !prev[ButtonIds.AGENTS],
          }));
        },
        type: ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled: hasParticipants || selectedSources.length > 0,
        isSelected: isFeatureSelected[ButtonIds.AGENTS],
      },
      {
        id: ButtonIds.CREATE_IMAGE,
        label: t('CREATE_IMAGE'),
        icon: <MediaImage width={20} height={20} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.CREATE_IMAGE]: !prev[ButtonIds.CREATE_IMAGE],
          }));
        },
        type: ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled: hasParticipants || selectedSources.length > 0,
        isSelected: isFeatureSelected[ButtonIds.CREATE_IMAGE],
      },
      {
        id: ButtonIds.WRITE_OR_EDIT,
        label: t('WRITE_OR_EDIT', { defaultValue: 'Write or edit' }),
        icon: <EditPencil width={20} height={20} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.WRITE_OR_EDIT]: !prev[ButtonIds.WRITE_OR_EDIT],
          }));
        },
        type: ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled: hasParticipants || selectedSources.length > 0,
        isSelected: isFeatureSelected[ButtonIds.WRITE_OR_EDIT],
      },
      {
        id: ButtonIds.SEARCH,
        label: t('SEARCH'),
        icon: <Internet width={20} height={20} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.SEARCH]: !prev[ButtonIds.SEARCH],
          }));
        },
        type: ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled:
          isFeatureSelected[ButtonIds.CREATE_IMAGE] ||
          hasParticipants ||
          selectedSources.length > 0,
        isSelected: isFeatureSelected[ButtonIds.SEARCH],
      },
      // {
      //   id: ButtonIds.VOICE,
      //   icon: <Mic size={16} />,
      //   onClick: () => {
      //     setIsFeatureSelected((prev) => ({
      //       ...initialFeaturesState,
      //       [ButtonIds.VOICE]: !prev[ButtonIds.VOICE],
      //     }));
      //   },
      //   type: ButtonTypes.ICON,
      //   alignment: Alignment.RIGHT,
      //   isDisabled: false,
      //   isSelected: isFeatureSelected[ButtonIds.VOICE],
      // },
    ],
    [isFeatureSelected, t, hasParticipants, selectedSources.length]
  );

  const hasLocalMessages = messages.length > 0;
  const isInitialConversationLoad = isLoading && !hasLocalMessages;
  const isResolvingConversation =
    !!conversationIdFromParams &&
    !isIncognitoChat &&
    isConversationQueryFetching &&
    !conversationFromUrl?.error &&
    !hasLocalMessages;
  const isVoiceCallInProgress = voiceCallStatus !== 'idle';

  if (
    !isVoiceCallInProgress &&
    (isInitialConversationLoad ||
      isCurrentPlanLoading ||
      isMyPlanLoading ||
      isLoadingParticipants ||
      isResolvingConversation ||
      (activeConversationId &&
        conversationIdFromParams &&
        activeConversationId !== conversationIdFromParams &&
        !hasLocalMessages))
  ) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <LoaderCircle className="animate-spin text-primary dark:text-true-white" />
      </div>
    );
  }

  if (conversationAccessError) {
    return <ConversationUnavailable />;
  }

  // No seat held (or NOPLAN) means no plan, unless the organization has free access.
  // Not gated by showSubscriptionInfo, which is hardcoded off while seat plans are live.
  if (!hasPlan) {
    return <NoPlanCard />;
  }

  const isGroupChatView =
    messages.length > 0 &&
    (Boolean(currentConversation?.isGroupChat) || cachedUsers.length > 1);
  const isDirectChatView = messages.length > 0 && !isGroupChatView;

  const isEmptyChatLanding = messages.length === 0;
  const dockInputToKeyboard =
    isMobile && isEmptyChatLanding && (isPromptFieldFocused || keepEmptyLandingDocked);
  const chatPageOverflow = chatPageOverflowClass(showDashboardCanvas, isEmptyChatLanding);

  return (
    <div
      className={cn(
        'flex h-full min-h-0 w-full flex-1 items-stretch',
        !showDashboardCanvas && 'relative',
        showDashboardCanvas && 'max-md:relative md:h-full md:flex-row md:gap-0 md:p-0',
        chatPageOverflow
      )}
    >
      <div
        data-testid="chat-page-column"
        className={cn(
          'relative flex h-full min-h-0 min-w-0 w-full flex-1 flex-col',
          isEmptyChatLanding
            ? 'items-center overflow-y-auto overflow-x-hidden'
            : 'overflow-hidden',
          viewedAssistantFile && isAssistantFileExpanded && 'hidden',
          !showDashboardCanvas && 'px-2 sm:px-6 md:px-8',
          showDashboardCanvas && 'px-2 sm:px-6 md:w-1/3 md:flex-none md:px-4 md:pb-4'
        )}
      >
        {showDashboardCanvas && !showDashboardHtml ? (
          <DashboardChatWorkingCursor
            agentWorking={isCurrentResponseGenerating}
            agentName={selectedAgentData?.agent?.name}
          />
        ) : null}
        {(isGroupChatView || isDirectChatView) && (
          <div
            ref={messagesScrollContainerRef}
            className={cn('flex min-h-0 w-full flex-1 justify-center', CHAT_MESSAGES_SCROLL_CLASS)}
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'hsl(var(--border) / 0.7) transparent',
            }}
          >
            <div className={`flex min-w-0 flex-col ${CHAT_MAIN_COLUMN_CLASS}`}>
              {isGroupChatView ? (
                <GroupChatMessages
                  onSendMessage={sendMessage}
                  messages={messages}
                  isLoading={isCurrentMessageLoading}
                  streamingMessage={streamingMessage}
                  isStillGenerating={isCurrentResponseGenerating}
                  chatInputHeight={chatInputHeight}
                  currentUser={currentUser}
                  participants={currentConversation?.participants || []}
                  removedParticipants={currentConversation?.removedParticipants || []}
                  associatedAssistant={associatedAssistant}
                  scrollContainerRef={messagesScrollContainerRef}
                />
              ) : (
                <ChatMessages
                  onSendMessage={sendMessage}
                  messages={messages}
                  isLoading={isCurrentMessageLoading}
                  streamingMessage={streamingMessage}
                  streamingAssistantMessageId={streamingAssistantMessageId}
                  isStillGenerating={isCurrentResponseGenerating}
                  chatInputHeight={chatInputHeight}
                  currentUser={currentUser}
                  associatedAssistant={associatedAssistant}
                  scrollButtonPortalHost={scrollButtonPortalHost}
                  scrollContainerRef={messagesScrollContainerRef}
                  agentModelLabel={selectedAgentId ? agentModelLabel : null}
                  agentId={selectedAgentId}
                  agentLogoUrl={selectedAgentData?.agent?.logo_url}
                />
              )}
            </div>
          </div>
        )}
        {isEmptyChatLanding && !showDashboardCanvas && (
          <div data-testid="empty-chat-top-spacer" className="min-h-0 w-full flex-1" aria-hidden />
        )}

        {isEmptyChatLanding && showDashboardCanvas && <DashboardChatPrompt />}

        {isEmptyChatLanding &&
          !showDashboardCanvas &&
          (!isIncognitoChat ? (
            <ChatGreeting
              className={cn('mb-8 w-full shrink-0 pt-6', CHAT_MAIN_COLUMN_CLASS)}
              title={greetingMessage || t('GENERAL_GREETINGS')}
            />
          ) : (
            <div className="mb-12 shrink-0 text-center">
              <span className="text-3xl font-normal">{t('INCOGNITO_CHAT')}</span>
              <p className="mt-1 text-sm text-muted-foreground max-w-md">
                {t('INCOGNITO_DESCRIPTION')}
              </p>
              <p className="mx-auto mt-3 max-w-md rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs font-normal text-muted-foreground">
                {t('INCOGNITO_DISCLOSURE', {
                  defaultValue:
                    'Incognito chat — not shown in history, not used for memory or training',
                })}
              </p>
            </div>
          ))}

        {connectionStatus === 'SESSION_EXPIRED' && (
          <div className={cn('mb-2 w-full shrink-0', CHAT_MAIN_COLUMN_CLASS)}>
            <div className="flex items-center gap-1 rounded-lg border border-red-400 bg-red-50 px-3 py-2">
              <TriangleAlert className="h-4 w-4 text-error" />
              <p className="text-xs font-normal text-error">
                {t('SESSION_EXPIRED_SIGN_IN_AGAIN', {
                  defaultValue: 'Session expired — please sign in again',
                })}
              </p>
            </div>
          </div>
        )}

        <div
          className={cn(
            'relative w-full shrink-0',
            messages?.length > 0 && isCurrentResponseGenerating
              ? 'sticky bottom-0 mt-auto backdrop-blur-sm'
              : messages?.length > 0
                ? 'sticky bottom-0 mt-auto'
                : '',
            isMobile ? 'z-10' : 'z-30'
          )}
          onFocusCapture={(event) => {
            if (isMobile && event.target instanceof HTMLTextAreaElement) {
              setIsPromptFieldFocused(true);
            }
          }}
          onBlurCapture={() => {
            requestAnimationFrame(() => {
              if (!(document.activeElement instanceof HTMLTextAreaElement)) {
                setIsPromptFieldFocused(false);
              }
            });
          }}
        >
          <div
            ref={setScrollButtonPortalHost}
            className={cn(
              'pointer-events-none absolute inset-x-0 bottom-0',
              isMobile ? 'z-20' : 'z-40'
            )}
          />

          <div className={`mx-auto min-w-0 ${CHAT_MAIN_COLUMN_CLASS}`}>
            <VoiceCallDockedBar />
            <OnboardingChatPanel
              disabled={isCurrentMessageLoading || isCurrentResponseGenerating}
            />
          </div>
          <div
            className={`relative mx-auto flex min-w-0 ${CHAT_MAIN_COLUMN_CLASS} items-center justify-center shadow-[var(--tw-inset-shadow),var(--tw-inset-ring-shadow),var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow)]`}
          >
            <ChatInput
              key={
                isCreateAgentMode
                  ? 'create-agent'
                  : onboardingRetryDraft
                    ? `onboarding-retry-${onboardingRetryDraft.version}`
                    : createAgentDraft || undefined
              }
              onSendMessage={sendMessage}
              isLoading={isCurrentMessageLoading}
              inputButtons={inputButtons}
              isCurrentResponseGenerating={isCurrentResponseGenerating}
              canCancelGeneration={canCancelGeneration}
              onHeightChange={setChatInputHeight}
              isFeatureSelected={isFeatureSelected}
              hasParticipants={hasParticipants}
              selectedSources={selectedSources}
              onSourcesChange={setSelectedSources}
              selectedFolderIdsBySource={selectedFolderIdsBySource}
              onSelectedFolderIdsBySourceChange={setSelectedFolderIdsBySource}
              isSourcesToolActive={isSourcesToolActive}
              onToggleTool={(toolId) => {
                setIsFeatureSelected((prev) => ({
                  ...prev,
                  [toolId]: !prev[toolId],
                }));
              }}
              onToggleSourcesTool={() => {
                setIsSourcesToolActive(!isSourcesToolActive);
              }}
              isCancelOnGoing={isCancelOnGoing}
              isModelSelectionDisabled={!!associatedAssistant}
              selectedTools={selectedTools}
              onSelectedToolsChange={setSelectedTools}
              showQuickActions={false}
              isCreateAgentMode={isCreateAgentMode}
              selectedAgentId={selectedAgentId}
              onAgentChange={handleAgentChange}
              showAgentSelector={
                !isOnboarding &&
                !isGroupChat &&
                !hasParticipants &&
                !associatedAssistant
              }
              showAgentProjectTab
              showMoreOptions={!isOnboarding}
              agentModelLabel={agentModelLabel}
              agentModelLoading={agentModelLoading}
              agentModelRegion={agentUploadModel?.region}
              agentUploadModel={agentUploadModel}
              initialDraft={onboardingRetryDraft?.text ?? createAgentDraft}
              agentUnavailable={agentUnavailable}
              onStartVoiceCall={canStartVoiceCall ? handleStartVoiceCall : undefined}
              isVoiceCallActive={voiceCallStatus !== 'idle'}
              chatTargetKey={`${conversationIdFromParams ?? 'new'}:${agentIdFromUrl ?? ''}`}
            />
          </div>
        </div>

        {isEmptyChatLanding &&
          !isOnboarding &&
          !hasParticipants &&
          !associatedAssistant &&
          !isDashboardConversation && (
            <CreateAgentSuggestions showDefaultAgents={!isCreateAgentMode} />
          )}

      {isEmptyChatLanding && !dockInputToKeyboard && !showDashboardCanvas && (
        <div data-testid="empty-chat-bottom-spacer" className="min-h-0 w-full flex-1" aria-hidden />
      )}

      {/* )} */}

      {/* Welcome Dialog */}
      <McpServiceAuthModal />

      <WelcomeDialog
        open={showWelcomeDialog}
        onOpenChange={setShowWelcomeDialog}
        onGetStarted={() => window.location.reload()}
      />
      <PaymentGracePeriodModal
        open={showPaymentGracePeriodModal}
        onOpenChange={setShowPaymentGracePeriodModal}
        onDismiss={() => setShowPaymentGracePeriodModal(false)}
        remainingDays={paymentGracePeriodDays}
      />
      {/* <NameRequiredModal
        open={showNameRequiredModal}
        onOpenChange={setShowNameRequiredModal}
        onDismiss={() => setShowNameRequiredModal(false)}
      /> */}
      </div>
      {showDashboardHtml && dashboardHtmlFile ? (
        <DashboardPreview
          file={dashboardHtmlFile}
          title={conversationFromUrl?.title}
          agentWorking={isCurrentResponseGenerating}
          agentName={selectedAgentData?.agent?.name}
        />
      ) : null}
      {showDashboardCanvas && !showDashboardHtml ? (
        <DashboardCanvas
          agentWorking={isCurrentResponseGenerating}
          agentName={selectedAgentData?.agent?.name}
        />
      ) : null}
      {viewedAssistantFile ? <AssistantFileViewer /> : null}
    </div>
  );
}